import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const fullScreenMessageStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  fontSize: '18px',
  color: '#666'
};

const AccessDenied = ({ message }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    padding: '32px',
    textAlign: 'center'
  }}>
    <h1 style={{ color: '#ef4444', marginBottom: '16px' }}>Access Denied</h1>
    <p style={{ color: '#666', marginBottom: '24px' }}>{message}</p>
    <button
      onClick={() => window.location.href = '/login'}
      style={{
        padding: '12px 24px',
        background: '#2AB3C0',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer'
      }}
    >
      Back to Login
    </button>
  </div>
);

const ProtectedRoute = ({ children, requireSyllySyncAdmin = false, requireOrgAdmin = false }) => {
  const {
    currentUser,
    isSyllySyncAdmin,
    isOrgAdminUser,
    isTeamMember,
    isOrgMember,
    loading,
    refreshAuth
  } = useAuth();
  const [checkingOrgAccess, setCheckingOrgAccess] = useState(false);
  const [checkedOrgAccess, setCheckedOrgAccess] = useState(false);

  const hasOrgDashboardAccess = isOrgAdminUser || isTeamMember || isOrgMember;

  useEffect(() => {
    if (!requireOrgAdmin || loading || !currentUser || hasOrgDashboardAccess) {
      setCheckingOrgAccess(false);
      setCheckedOrgAccess(false);
      return;
    }

    let cancelled = false;
    setCheckingOrgAccess(true);
    setCheckedOrgAccess(false);

    refreshAuth()
      .catch((err) => {
        console.error('Error refreshing organization access:', err);
      })
      .finally(() => {
        if (!cancelled) {
          setCheckingOrgAccess(false);
          setCheckedOrgAccess(true);
        }
      });

    return () => {
      cancelled = true;
    };
    // refreshAuth is intentionally omitted because AuthContext recreates it each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requireOrgAdmin, loading, currentUser?.uid, hasOrgDashboardAccess]);

  if (loading) {
    return <div style={fullScreenMessageStyle}>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (requireSyllySyncAdmin && !isSyllySyncAdmin) {
    return <AccessDenied message="You need a @syllysync.com email to access this dashboard." />;
  }

  // For org dashboard routes, allow admins, team members, and approved members.
  // When an org was just created, Firestore/AuthContext may need one refresh before
  // admin access is visible; show a loading state instead of flashing Access Denied.
  if (requireOrgAdmin && !hasOrgDashboardAccess) {
    if (checkingOrgAccess || !checkedOrgAccess) {
      return <div style={fullScreenMessageStyle}>Checking organization access...</div>;
    }

    return <AccessDenied message="You are not authorized to access the organization dashboard." />;
  }

  return children;
};

export default ProtectedRoute;
